// src/components/FilterPanel.jsx
import React, { memo, useState } from "react";


import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Badge } from "primereact/badge";

import {
  getThreeMonthsAgo,
  validateDateRange,

} from "../components/paymentUtils";

const statusOptions = [
  { label: "All Statuses", value: null },
  { label: "Success", value: "SUCCESS" },
  { label: "Failed", value: "FAILED" },
  { label: "Pending", value: "PENDING" },
  { label: "Processing", value: "PROCESSING" },
];

const FilterPanel = memo(
  ({
    visible,
    filters,
    onFilterChange,
    onClear,
    onApply,
    onExport,
    isExporting,
    toastRef,
  }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (!visible) return null;

    const handleDateChange = (field, value) => {
      const newFilters = { ...filters, [field]: value };

      if (newFilters.start_date && newFilters.end_date) {
        if (!validateDateRange(newFilters.start_date, newFilters.end_date)) {
          toastRef.current?.show({
            severity: "warn",
            summary: "Date Range Warning",
            detail: "Selected date range exceeds 3 months.",
            life: 3000,
          });
        }
      }
      onFilterChange(field, value);
    };

    const getMaxDateForCalendar = (field) => {
      if (field === "start_date" && filters.end_date) {
        return filters.end_date;
      }
      return new Date();
    };

    const getMinDateForCalendar = (field) => {
      if (field === "end_date" && filters.start_date) {
        return filters.start_date;
      }
      return getThreeMonthsAgo();
    };

    const hasDateFilterApplied = filters.start_date && filters.end_date;
    const isDateRangeCurrentlyValid =
      hasDateFilterApplied &&
      validateDateRange(filters.start_date, filters.end_date);

    // Count active filters for badge
    const getActiveFiltersCount = () => {
      let count = 0;
      if (filters.order_id) count++;
      if (filters.payment_status) count++;
      if (filters.start_date && filters.end_date) count++;
      return count;
    };

    const activeFiltersCount = getActiveFiltersCount();

    return (
      <div className="filter-panel-container">
        <Card
          className="filter-panel-card"
          style={{
            borderRadius: 6,
            minHeight: "auto",
            padding: "8px",
            display: "block",
            maxWidth: "100%",
          }}
        >
          {/* Header */}
          <div className="filter-header">
            <div className="filter-header-left">
              <i className="pi pi-filter filter-icon"></i>
              <span className="filter-title">Search & Filter</span>
              {activeFiltersCount > 0 && (
                <Badge
                  value={activeFiltersCount}
                  severity="info"
                  className="filter-badge"
                />
              )}
            </div>
            <div className="filter-header-right">
              {activeFiltersCount > 0 && (
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm header-btn"
                  onClick={onClear}
                  tooltip="Clear all filters"
                  tooltipOptions={{ position: "top" }}
                />
              )}
              <Button
                icon={isCollapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
                className="p-button-text p-button-sm header-btn"
                onClick={() => setIsCollapsed(!isCollapsed)}
                tooltip={isCollapsed ? "Expand filters" : "Collapse filters"}
                tooltipOptions={{ position: "top" }}
              />
            </div>
          </div>

          {!isCollapsed && (
            <>
              <Divider className="filter-divider" />

              {/* Filter Controls - Optimized Grid Layout */}
              <div className="filter-grid">
                <div className="filter-item">
                  <label htmlFor="orderIdFilter" className="filter-label">
                    <i className="pi pi-search label-icon"></i>
                    Transaction ID
                  </label>
                  <InputText
                    id="orderIdFilter"
                    value={filters.order_id || ""}
                    onChange={(e) =>
                      onFilterChange("order_id", e.target.value)
                    }
                    placeholder="Enter transaction ID"
                    className="filter-input-field"
                  />
                </div>

                <div className="filter-item">
                  <label htmlFor="statusFilter" className="filter-label">
                    <i className="pi pi-info-circle label-icon"></i>
                    Status
                  </label>
                  <Dropdown
                    id="statusFilter"
                    value={filters.payment_status}
                    options={statusOptions}
                    onChange={(e) =>
                      onFilterChange("payment_status", e.value)
                    }
                    placeholder="Select status"
                    className="filter-dropdown-field"
                    showClear
                  />
                </div>

                <div className="filter-item">
                  <label htmlFor="startDateFilter" className="filter-label">
                    <i className="pi pi-calendar label-icon"></i>
                    Start Date
                  </label>
                  <Calendar
                    id="startDateFilter"
                    value={filters.start_date}
                    onChange={(e) =>
                      handleDateChange("start_date", e.value)
                    }
                    placeholder="Select start date"
                    dateFormat="dd/mm/yy"
                    maxDate={getMaxDateForCalendar("start_date")}
                    minDate={getMinDateForCalendar("start_date")}
                    showIcon
                    readOnlyInput
                    className="filter-calendar-field"
                    showClear
                  />
                </div>

                <div className="filter-item">
                  <label htmlFor="endDateFilter" className="filter-label">
                    <i className="pi pi-calendar label-icon"></i>
                    End Date
                  </label>
                  <Calendar
                    id="endDateFilter"
                    value={filters.end_date}
                    onChange={(e) => handleDateChange("end_date", e.value)}
                    placeholder="Select end date"
                    dateFormat="dd/mm/yy"
                    maxDate={getMaxDateForCalendar("end_date")}
                    minDate={getMinDateForCalendar("end_date")}
                    
                    showIcon
                    readOnlyInput
                    className="filter-calendar-field"
                    showClear
                  />
                </div>
              </div>

              {/* Date Range Warning */}
              {hasDateFilterApplied && !isDateRangeCurrentlyValid && (
                <div className="warning-message">
                  <i className="pi pi-exclamation-triangle warning-icon"></i>
                  <span>
                    Date range cannot exceed 3 months for optimal performance.
                  </span>
                </div>
              )}

              <Divider className="filter-divider" />

              {/* Action Buttons */}
              <div className="action-buttons">
                <div className="action-left">
                  <Button
                    label="Clear All"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-secondary action-btn"
                    onClick={onClear}
                    disabled={activeFiltersCount === 0}
                  />
                  <Button
                    label="Apply"
                    icon="pi pi-check"
                    className="p-button-primary action-btn"
                    onClick={onApply}
                    disabled={
                      hasDateFilterApplied && !isDateRangeCurrentlyValid
                    }
                  />
                </div>

                <div className="action-right">
                  <Button
                    label="Export Excel"
                    icon="pi pi-file-excel"
                    className="p-button-success action-btn"
                    onClick={onExport}
                    loading={isExporting}
                    disabled={
                      !hasDateFilterApplied || !isDateRangeCurrentlyValid
                    }
                    tooltip={
                      !hasDateFilterApplied
                        ? "Please select both start and end dates to export."
                        : !isDateRangeCurrentlyValid
                        ? "Date range for export cannot exceed 3 months."
                        : "Export filtered data to Excel"
                    }
                    tooltipOptions={{ position: "top" }}
                  />
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Enhanced CSS with adjusted font sizes for placeholders, dropdown, and dates */}
        <style jsx>{`
          .filter-panel-container {
            margin-bottom: 0.75rem;
            width: 100%;
          }

          .filter-panel-card {
            border: 1px solid #e5e7eb;
            border-radius: 4px;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
              0 1px 2px 0 rgba(0, 0, 0, 0.06);
            background: #ffffff;
            padding: 0.75rem;
          }

          .filter-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
            padding: 0;
            min-height: 2rem;
          }

          .filter-header-left {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .filter-header-right {
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }

          .filter-icon {
            color: #3b82f6;
            font-size: 0.875rem;
          }

          .filter-title {
            font-weight: 600;
            font-size: 0.875rem;
            color: #374151;
            margin: 0;
            line-height: 1.25;
          }

          .filter-badge {
            margin-left: 0.25rem;
            font-size: 0.75rem;
          }

          .header-btn {
            padding: 0.25rem;
            margin: 0;
            min-width: auto;
            width: 1.75rem;
            height: 1.75rem;
            font-size: 0.75rem;
          }

          .filter-divider {
            margin: 0.5rem 0;
          }

          .filter-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 0.75rem;
            margin-bottom: 0.75rem;
          }

          .filter-item {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 0;
          }

          .filter-label {
            font-weight: 500;
            font-size: 0.75rem;
            color: #374151;
            display: flex;
            align-items: center;
            margin-bottom: 0.125rem;
            line-height: 1.2;
          }

          .label-icon {
            font-size: 0.625rem;
            margin-right: 0.25rem;
            color: #6b7280;
          }

          .filter-input-field,
          .filter-dropdown-field,
          .filter-calendar-field {
            font-size: 0.6125rem; /* Increased from 0.75rem */
            padding: 0.375rem 0.5rem;
            border-radius: 4px;
            border: 1px solid #d1d5db;
            transition: border-color 0.15s ease-in-out,
              box-shadow 0.15s ease-in-out;
            width: 100%;
            box-sizing: border-box;
            height: 2.5rem;
            line-height: 1.25;
          }

          /* Specific placeholder styling */
          .filter-input-field::placeholder {
            font-size: 0.8125rem; /* Increased from 0.75rem */
            color: #9ca3af;
            opacity: 1;
          }

          /* PrimeReact Dropdown specific styles */
          .filter-dropdown-field .p-dropdown-label {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
            line-height: 1.25 !important;
          }

          .filter-dropdown-field .p-dropdown-label.p-placeholder {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
            color: #9ca3af !important;
          }

          /* PrimeReact Dropdown panel options */
          :global(.p-dropdown-panel .p-dropdown-items .p-dropdown-item) {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
            padding: 0.5rem 0.75rem !important;
          }

          /* PrimeReact Calendar specific styles */
          .filter-calendar-field .p-inputtext {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
            line-height: 1.25 !important;
          }

          .filter-calendar-field .p-inputtext::placeholder {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
            color: #9ca3af !important;
          }

          /* Calendar date picker panel */
          :global(.p-datepicker) {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
          }

          :global(.p-datepicker .p-datepicker-calendar td span) {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
          }

          :global(.p-datepicker .p-monthpicker .p-monthpicker-month) {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
          }

          :global(.p-datepicker .p-yearpicker .p-yearpicker-year) {
            font-size: 0.8125rem !important; /* Increased from 0.75rem */
          }

          .filter-input-field:focus,
          .filter-dropdown-field:focus,
          .filter-calendar-field:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
          }

          .warning-message {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 4px;
            padding: 0.5rem;
            color: #92400e;
            font-size: 0.75rem;
            display: flex;
            align-items: flex-start;
            margin: 0.5rem 0;
            line-height: 1.4;
          }

          .warning-icon {
            margin-right: 0.375rem;
            margin-top: 0.0625rem;
            flex-shrink: 0;
            font-size: 0.75rem;
          }

          .action-buttons {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.75rem;
            margin-top: 0.5rem;
          }

          .action-left,
          .action-right {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }

          .action-btn {
            font-size: 0.75rem;
            padding: 0.375rem 0.75rem;
            border-radius: 4px;
            font-weight: 500;
            transition: all 0.15s ease-in-out;
            min-width: auto;
            white-space: nowrap;
            height: 2rem;
            line-height: 1.25;
          }

          .action-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          /* Tablet Responsive */
          @media (max-width: 1024px) {
            .filter-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 0.625rem;
            }

            .filter-input-field,
            .filter-dropdown-field,
            .filter-calendar-field {
              font-size: 0.8125rem; /* Maintain consistent font size */
            }

            .filter-input-field::placeholder {
              font-size: 0.8125rem;
            }

            .filter-dropdown-field .p-dropdown-label,
            .filter-dropdown-field .p-dropdown-label.p-placeholder {
              font-size: 0.8125rem !important;
            }

            .filter-calendar-field .p-inputtext,
            .filter-calendar-field .p-inputtext::placeholder {
              font-size: 0.8125rem !important;
            }
          }

          /* Enhanced Mobile Compatibility */
          @media (max-width: 768px) {
            .filter-panel-card {
              padding: 0.625rem;
              margin: 0 0.25rem;
            }

            .filter-grid {
              grid-template-columns: 1fr;
              gap: 0.625rem;
            }

            .filter-header {
              flex-wrap: wrap;
              gap: 0.375rem;
              min-height: auto;
            }

            .filter-title {
              font-size: 0.8125rem;
            }

            .filter-input-field,
            .filter-dropdown-field,
            .filter-calendar-field {
              font-size: 0.8125rem; /* Maintain consistent font size on mobile */
            }

            .filter-input-field::placeholder {
              font-size: 0.8125rem;
            }

            .filter-dropdown-field .p-dropdown-label,
            .filter-dropdown-field .p-dropdown-label.p-placeholder {
              font-size: 0.8125rem !important;
            }

            .filter-calendar-field .p-inputtext,
            .filter-calendar-field .p-inputtext::placeholder {
              font-size: 0.8125rem !important;
            }

            .action-buttons {
              flex-direction: column;
              align-items: stretch;
              gap: 0.625rem;
            }

            .action-left,
            .action-right {
              justify-content: center;
              flex-wrap: wrap;
            }

            .action-btn {
              flex: 1;
              min-width: 80px;
              font-size: 0.6875rem;
              padding: 0.375rem 0.5rem;
            }
          }

          @media (max-width: 480px) {
            .filter-panel-card {
              padding: 0.5rem;
              margin: 0 0.125rem;
            }

            .filter-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;
            }

            .filter-header-right {
              align-self: flex-end;
            }

            .filter-title {
              font-size: 0.75rem;
            }

            .filter-label {
              font-size: 0.6875rem;
            }

            .filter-input-field,
            .filter-dropdown-field,
            .filter-calendar-field {
              font-size: 0.75rem; /* Slightly smaller on very small screens */
              padding: 0.3125rem 0.4375rem;
              height: 1.875rem;
            }

            .filter-input-field::placeholder {
              font-size: 0.75rem;
            }

            .filter-dropdown-field .p-dropdown-label,
            .filter-dropdown-field .p-dropdown-label.p-placeholder {
              font-size: 0.75rem !important;
            }

            .filter-calendar-field .p-inputtext,
            .filter-calendar-field .p-inputtext::placeholder {
              font-size: 0.75rem !important;
            }

            .action-left,
            .action-right {
              flex-direction: column;
              width: 100%;
            }

            .action-btn {
              width: 100%;
              justify-content: center;
              font-size: 0.6875rem;
              height: 1.875rem;
            }

            .warning-message {
              font-size: 0.6875rem;
              padding: 0.375rem;
            }

            .warning-icon {
              font-size: 0.6875rem;
            }
          }

          /* Better Cross-browser Compatibility */
          @supports not (grid-template-columns: subgrid) {
            .filter-grid {
              display: -webkit-box;
              display: -ms-flexbox;
              display: flex;
              -webkit-box-orient: horizontal;
              -webkit-box-direction: normal;
              -ms-flex-direction: row;
              flex-direction: row;
              -ms-flex-wrap: wrap;
              flex-wrap: wrap;
            }

            .filter-item {
              -webkit-box-flex: 1;
              -ms-flex: 1 1 200px;
              flex: 1 1 200px;
              min-width: 200px;
            }
          }

          /* IE11 Compatibility */
          @media screen and (-ms-high-contrast: active),
            (-ms-high-contrast: none) {
            .filter-grid {
              display: -ms-flexbox;
              -ms-flex-wrap: wrap;
            }

            .filter-item {
              -ms-flex: 1 1 200px;
              max-width: calc(25% - 0.5625rem);
            }
          }

          /* Print Styles */
          @media print {
            .filter-panel-container {
              display: none;
            }
          }
        `}</style>
      </div>
    );
  }
);

FilterPanel.displayName = "FilterPanel";

export default FilterPanel;
